import React from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';

type MetaItem = {
  label: string;
  value: React.ReactNode;
};

type Props = {
  /** 表示・コピー対象のID（必須） */
  id: string;
  /** IDの見出し（既定: ID） */
  idLabel?: string;
  /** 作成者・作成日などのメタ情報（任意） */
  metaItems?: MetaItem[];
};

/**
 * 詳細/フォームの最上部に表示するメタ情報ヘッダー。
 * ID表示 + コピー機能（必須）と、作成者・作成日などの補足情報を上部にまとめる。
 * コンソールドロワー内でヘッダーを隠す場合に、失われるID/コピー/メタをここで担保する。
 * 下線で本文と区切る上部配置のメタ情報ヘッダー。
 */
const DetailMetaHeader: React.FC<Props> = ({ id, idLabel = 'ID', metaItems = [] }) => {
  const [copyTooltip, setCopyTooltip] = React.useState('Copy to clipboard');

  const handleCopy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopyTooltip('Copied!');
      setTimeout(() => setCopyTooltip('Copy to clipboard'), 2000);
    });
  };

  return (
    <Box>
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {idLabel}:
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {id}
          </Typography>
          <Tooltip title={copyTooltip}>
            <IconButton size="small" onClick={handleCopy} sx={{ flexShrink: 0, p: 0.25 }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        {metaItems.map((m) => (
          <Typography key={m.label} variant="caption" color="text.secondary">
            {m.label}: {m.value}
          </Typography>
        ))}
      </Stack>
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
};

export default DetailMetaHeader;
